import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import Link from "next/link";
import { useLocale } from "next-intl";
import React, { useEffect, useRef, useState } from "react";
import { useBreakpoint } from "@/lib/breakpoints";
import { cn } from "@/lib/utils";
import Arrow from "./ui/arrow";
import VariableImage from "./ui/VariableImage";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const HorizontalSlider = ({ data }: { data: any }) => {
  const homeHorizontalSliderRef = useRef<HTMLDivElement>(null);
  const horizontalPinTl = useRef<any>(null);

  const locale = useLocale();
  const { isDesktop } = useBreakpoint();
  const sectionData = data?.SectionFour_Data;

  const [slideTitles, setSlideTitles] = useState<HTMLElement[]>([]);
  const [slideTexts, setSlideTexts] = useState<HTMLElement[]>([]);
  const [slideImgs, setSlideImgs] = useState<HTMLImageElement[]>([]);
  const [slideImgs2, setSlideImgs2] = useState<HTMLImageElement[]>([]);
  const [pinDuration, setPinDuration] = useState<number>(0);
  const [index, setIndex] = useState<number>(0);

  useEffect(() => {
    setSlideTitles(gsap.utils.toArray("#homeHorizontalSlider .slide-title"));
    setSlideTexts(gsap.utils.toArray("#homeHorizontalSlider .slide-text"));
    setSlideImgs(gsap.utils.toArray("#homeHorizontalSlider .slide-img"));
    setSlideImgs2(gsap.utils.toArray("#homeHorizontalSlider .slide-img-2"));
  }, []);

  useEffect(() => {
    setPinDuration(
      isDesktop ? slideTitles.length * 1000 : slideTitles.length * 500,
    );
  }, [isDesktop, slideTitles]);

  const { contextSafe } = useGSAP(
    () => {
      if (
        !homeHorizontalSliderRef.current &&
        !slideTitles.length &&
        !slideTexts.length &&
        !slideImgs.length &&
        !slideImgs2.length &&
        !pinDuration
      )
        return;

      gsap.set(slideImgs, { zIndex: (i: number) => slideImgs.length - i });
      gsap.set(slideImgs2, { zIndex: (i: number) => slideImgs2.length - i });

      horizontalPinTl.current = gsap.timeline({
        scrollTrigger: {
          trigger: "#homeHorizontalSlider",
          start: "center center",
          end: `+=${pinDuration}`,
          scrub: 2,
          pin: true,
          pinSpacing: "margin",
          invalidateOnRefresh: true,
        },
        defaults: { ease: "none" },
      });

      const lastIndex = slideTitles.length - 1;

      slideTitles.forEach((slide: any, i: number) => {
        horizontalPinTl.current.to([slideTitles[i], slideTexts[i]], {
          autoAlpha: 1,
          x: 0,
        });

        horizontalPinTl.current.addLabel(`label-${i}`);

        horizontalPinTl.current.to([slideTitles[i], slideTexts[i]], {
          autoAlpha: i === lastIndex ? 1 : 0,
          x: i === lastIndex ? 0 : -20,
        });

        horizontalPinTl.current.to(
          [slideImgs[i], slideImgs2[i]],
          {
            clipPath:
              i === lastIndex ? "inset(0% 0% 0% 0%)" : "inset(0% 100% 0% 0%)",
            scale: 1,
          },
          "<",
        );
      });
    },
    {
      dependencies: [
        data,
        slideTitles,
        slideTexts,
        slideImgs,
        slideImgs2,
        pinDuration,
      ],
      revertOnUpdate: true,
      scope: homeHorizontalSliderRef,
    },
  );

  useGSAP(
    () => {
      if (!homeHorizontalSliderRef.current && !pinDuration) return;
      ScrollTrigger.create({
        trigger: homeHorizontalSliderRef.current,
        start: "center center",
        end: `+=${pinDuration}`,
        scrub: 1,
        pin: "#mainBg",
        pinSpacing: false,
      });
    },
    {
      dependencies: [pinDuration],
      revertOnUpdate: true,
    },
  );

  const nextSlide = contextSafe(() => {
    const newIndex = index !== slideTitles.length - 1 ? index + 1 : index;
    setIndex(newIndex);
    gsap.to(window, {
      scrollTo: horizontalPinTl.current.scrollTrigger?.labelToScroll(
        `label-${newIndex}`,
      ),
    });
  });

  const prevSlide = contextSafe(() => {
    const newIndex = index !== 0 ? index - 1 : index;
    setIndex(newIndex);
    gsap.to(window, {
      scrollTo: horizontalPinTl.current.scrollTrigger?.labelToScroll(
        `label-${newIndex}`,
      ),
    });
  });
  return (
    <section ref={homeHorizontalSliderRef} className="relative">
      <div className="relative flex flex-col gap-10 pt-20 lg:gap-40 lg:pt-[170px]">
        <div className="container flex-center flex-col text-center">
          <div className="seperator border-black pb-10"></div>
          <h2 className="title-70 lg:max-w-[900px]">
            {data?.SectionFour_Title?.Text}
          </h2>
        </div>

        <div
          id="homeHorizontalSlider"
          className="relative flex h-screen flex-wrap overflow-hidden"
        >
          <div className="absolute bottom-0 z-10 flex size-full h-[50vh] flex-col justify-between p-5 text-white lg:h-screen lg:w-1/2 lg:p-12 2xl:px-16">
            <div className="relative h-10">
              {sectionData.map((slide: any, i: number) => (
                <h3
                  key={i}
                  className={cn(
                    "title-30 slide-title absolute",
                    i !== 0 && "invisible absolute -translate-x-20 opacity-0",
                  )}
                >
                  {slide.TabName}
                </h3>
              ))}
            </div>
            <div className="seperator mb-auto border-white"></div>
            <div className="relative mb-auto">
              {sectionData.map((slide: any, i: number) => (
                <div
                  key={i}
                  className={cn(
                    "slide-text top-0",
                    i !== 0 && "invisible absolute translate-x-20 opacity-0",
                  )}
                >
                  <p className="capitalize! mb-5 line-clamp-3 font-semibold lg:mb-10 lg:line-clamp-none">
                    {slide.Title}
                  </p>
                  <p className="mb-5 line-clamp-3 whitespace-pre-line text-sm lg:mb-10 lg:line-clamp-none 2xl:text-lg">
                    {slide.Description}
                  </p>

                  <div className="flex flex-wrap gap-3">
                    {slide.Link.map((link: any, i: number) => (
                      <Link
                        key={i}
                        href={link.LinkTo}
                        className="slide-link text-sm capitalize underline lg:text-lg"
                        onClick={() => {
                          window.dataLayer = window.dataLayer || [];
                          window.dataLayer.push({
                            event: "cta_button_click",
                            button_name: link.Text,
                            section: slide.TabName,
                          });
                        }}
                      >
                        {link.Text}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-5 rtl:flex-row-reverse rtl:justify-end">
              <button onClick={prevSlide} className="size-10 lg:size-16">
                <Arrow fill="#fff" />
              </button>

              <button onClick={nextSlide} className="size-10 lg:size-16">
                <Arrow fill="#fff" arrowType="next" />
              </button>
            </div>
          </div>

          <div className="absolute bottom-0 z-8 hidden h-[60vh] w-full blur-custom will-change-auto lg:block lg:h-full lg:w-1/2 lg:p-20"></div>

          <div
            className={cn("size-full", locale === "ar" ? "scale-x-[-1]" : "")}
          >
            {sectionData.map((slide: any, i: number) => (
              <VariableImage
                key={i}
                src={slide.Image?.data?.attributes?.url}
                width={1920}
                height={1080}
                className="slide-img absolute top-0 left-0 h-[50vh] w-full object-cover object-right will-change-auto lg:h-screen"
                alt={slide.Image?.data?.attributes?.alternativeText}
                caption={slide.Image?.data?.attributes?.caption}
              />
            ))}
          </div>

          {!isDesktop &&
            sectionData.map((slide: any, i: number) => (
              <VariableImage
                key={i}
                src={slide.Image?.data?.attributes?.url}
                width={1920}
                height={1080}
                className="slide-img-2 absolute bottom-0 left-0 -z-10 h-[50vh] w-full object-cover object-right blur-xl will-change-auto lg:h-screen"
                alt={slide.Image?.data?.attributes?.alternativeText}
                caption={slide.Image?.data?.attributes?.caption}
              />
            ))}
        </div>
      </div>
    </section>
  );
};

export default HorizontalSlider;
